import { Component, OnInit, ViewChild } from '@angular/core';
import { PoBreadcrumb, PoDialogService, PoModalComponent, PoNotificationService } from '@po-ui/ng-components';
import { finalize } from 'rxjs';
import { AssignRolesBatchResponse, UserService } from './user.service';

interface RoleItem {
    id: string;
    name: string;
    description?: string;
}

@Component({
    selector: 'erpcloud-user',
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.css'],
    standalone: false
})
export class UserComponent implements OnInit {

    @ViewChild('newUserModal') newUserModal!: PoModalComponent;
    @ViewChild('rolesModal') rolesModal!: PoModalComponent;
    @ViewChild('assignRolesModal') assignRolesModal!: PoModalComponent;

    loading = false;
    users: any[] = [];
    filteredUsers: any[] = [];
    roles: RoleItem[] = [];
    public rolesLoading = false;
    availableRoles: RoleItem[] = [];
    availableRolesLoading = false;
    pendingRoles: RoleItem[] = [];
    savingRoles = false;
    selectedUser: any = null;
    search = '';
    newUser = { email: '', nomeCompleto: '' };
    deletingUserEmail: string | null = null;
    togglingUserEmail: string | null = null;
    creatingUser = false;

    readonly breadcrumb: PoBreadcrumb = {
        items: [{ label: 'Home', link: '/home' }, { label: 'Controle de Acesso' }, { label: 'Usuários' }]
    };

    constructor(
        private readonly userService: UserService,
        private readonly poNotification: PoNotificationService,
        private readonly poDialogService: PoDialogService
    ) { }

    ngOnInit(): void {
        this.refreshData();
    }

    refreshData() {
        this.loadUsers();
    }

    loadUsers() {
        this.loading = true;
        this.userService.getClientAppUsers().subscribe({
            next: (users) => {
                this.users = (users || []).map(u => {
                    const enabledInApp = u.enabledInApp ?? u.EnabledInApp;
                    const ativadaGlobal = u.ativada ?? u.activated ?? u.Ativada;
                    // Preferência: status no Ecco (EnabledInApp). Fallback: conta ativa no OAuth.
                    const ativo = enabledInApp != null ? !!enabledInApp : !!ativadaGlobal;

                    return {
                        ...u,
                        nome: u.nome ?? u.nomeCompleto ?? u.name ?? '',
                        email: u.email ?? '',
                        ativo,
                    ativada: ativo ? 'Ativo' : 'Inativo',
                    actions: ''
                    };
                });
                this.applySearch();
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.poNotification.error('Não foi possível carregar usuários.');
            }
        });
    }

    private catalogRoleIdFromUserAppRole(r: any): string {
        const v =
            r?.roleId ??
            r?.RoleId ??
            r?.role?.id ??
            r?.role?.Id ??
            r?.Role?.id ??
            r?.Role?.Id;
        const s = v != null && v !== '' ? String(v).trim() : '';
        if (!s || s === '00000000-0000-0000-0000-000000000000') {
            return '';
        }
        return s;
    }

    private mapRoleItem(r: any): RoleItem {
        return {
            id: String(r.id ?? r.roleId ?? r.Id ?? '').trim(),
            name: r.name ?? r.nome ?? r.roleName ?? r.role?.name ?? 'Sem nome',
            description: r.description ?? r.descricao ?? r.Details ?? r.detail ?? ''
        };
    }

    loadRolesByUser(userId: number, onLoaded?: () => void) {
        this.rolesLoading = true;
        this.userService.getUserRoles(userId).subscribe({
            next: (roles) => {
                this.roles = (roles || []).map(r => ({
                    ...this.mapRoleItem(r),
                    id: this.catalogRoleIdFromUserAppRole(r) || this.mapRoleItem(r).id
                })).filter(r => !!r.id);
                this.rolesLoading = false;
                onLoaded?.();
            },
            error: () => {
                this.rolesLoading = false;
                this.roles = [];
                this.poNotification.warning('Não foi possível carregar as permissões do usuário.');
            }
        });
    }

    applySearch() {
        const term = this.search.trim().toLowerCase();
        if (!term) {
            this.filteredUsers = [...this.users];
            return;
        }

        this.filteredUsers = this.users.filter(u =>
            String(u.nome ?? '').toLowerCase().includes(term) ||
            String(u.email ?? '').toLowerCase().includes(term)
        );
    }

    openNewUserModal() {
        this.newUser = { email: '', nomeCompleto: '' };
        this.newUserModal.open();
    }

    createUser() {
        if (!this.newUser.email || !this.newUser.nomeCompleto) {
            this.poNotification.warning('Informe nome e e-mail para criar o usuário.');
            return;
        }
        if (this.creatingUser) {
            return;
        }
        this.creatingUser = true;
        this.userService.createClientAppUser(this.newUser).pipe(
            finalize(() => {
                this.creatingUser = false;
            })
        ).subscribe({
            next: () => {
                this.poNotification.success('Usuário criado com sucesso.');
                this.newUserModal.close();
                this.loadUsers();
            },
            error: () => this.poNotification.error('Não foi possível criar o usuário.')
        });
    }

    removeUser(user: any) {
        const email = String(user?.email ?? '').trim();
        if (!email || this.deletingUserEmail !== null || this.togglingUserEmail !== null) {
            return;
        }

        this.poDialogService.confirm({
            title: 'Remover usuário',
            message: `Tem certeza que deseja remover permanentemente o usuário ${email}? Esta ação não é a mesma de inativar.`,
            confirm: () => {
                this.deletingUserEmail = email;
                this.userService.removeClientAppUser(email).pipe(
                    finalize(() => {
                        this.deletingUserEmail = null;
                    })
                ).subscribe({
                    next: () => {
                        this.poNotification.success('Usuário removido com sucesso.');
                        this.loadUsers();
                    },
                    error: () => this.poNotification.error('Não foi possível remover o usuário.')
                });
            }
        });
    }

    toggleUserEnabled(user: any) {
        const email = String(user?.email ?? '').trim();
        if (!email || this.togglingUserEmail !== null || this.deletingUserEmail !== null) {
            return;
        }

        const ativar = !user.ativo;
        const titulo = ativar ? 'Reativar usuário' : 'Inativar usuário';
        const mensagem = ativar
            ? `Deseja reativar o acesso de ${email} ao Ecco?`
            : `Deseja inativar o acesso de ${email} ao Ecco? O usuário deixa de acessar o sistema, mas pode ser reativado depois.`;

        this.poDialogService.confirm({
            title: titulo,
            message: mensagem,
            confirm: () => {
                this.togglingUserEmail = email;
                this.userService.setClientAppUserEnabled(email, ativar).pipe(
                    finalize(() => {
                        this.togglingUserEmail = null;
                    })
                ).subscribe({
                    next: () => {
                        this.poNotification.success(
                            ativar ? 'Usuário reativado com sucesso.' : 'Usuário inativado com sucesso.'
                        );
                        this.loadUsers();
                    },
                    error: (err) => {
                        const apiError = err?.error?.error || err?.error?.message;
                        this.poNotification.error(
                            apiError || (ativar
                                ? 'Não foi possível reativar o usuário.'
                                : 'Não foi possível inativar o usuário.')
                        );
                    }
                });
            }
        });
    }

    openRolesModal(user: any) {
        this.selectedUser = user;
        const userId = Number(this.selectedUser?.id ?? this.selectedUser?.Id ?? 0);
        this.roles = [];
        if (!userId) {
            this.poNotification.warning('Não foi possível identificar o usuário selecionado.');
            return;
        }
        this.loadRolesByUser(userId);
        this.rolesModal.open();
    }

    openAssignRolesModal() {
        const userId = Number(this.selectedUser?.id ?? this.selectedUser?.Id ?? 0);
        if (!userId) {
            this.poNotification.warning('Não foi possível identificar o usuário.');
            return;
        }
        this.pendingRoles = [];
        this.loadAvailableRolesToAssign();
        this.assignRolesModal.open();
    }

    fecharAssignRolesModal() {
        if (this.pendingRoles.length > 0) {
            this.poDialogService.confirm({
                title: 'Descartar alterações?',
                message: 'Existem roles selecionadas que ainda não foram salvas. Deseja sair sem salvar?',
                confirm: () => {
                    this.pendingRoles = [];
                    this.assignRolesModal.close();
                }
            });
            return;
        }
        this.assignRolesModal.close();
    }

    private loadAvailableRolesToAssign() {
        this.availableRolesLoading = true;
        this.availableRoles = [];
        this.userService.getClientAppRoles().subscribe({
            next: (allRoles) => {
                const mapped = (allRoles || []).map(r => this.mapRoleItem(r)).filter(r => !!r.id);
                const indisponiveis = new Set([
                    ...this.roles.map(r => r.id),
                    ...this.pendingRoles.map(r => r.id)
                ]);
                this.availableRoles = mapped.filter(r => !indisponiveis.has(r.id));
                this.availableRolesLoading = false;
            },
            error: () => {
                this.availableRolesLoading = false;
                this.poNotification.error('Não foi possível carregar as roles disponíveis.');
            }
        });
    }

    adicionarRolePendente(role: RoleItem) {
        const id = String(role.id ?? '').trim();
        if (!id || this.pendingRoles.some(r => r.id === id)) {
            return;
        }
        this.pendingRoles = [...this.pendingRoles, role];
        this.availableRoles = this.availableRoles.filter(r => r.id !== id);
    }

    removerRolePendente(role: RoleItem) {
        this.pendingRoles = this.pendingRoles.filter(r => r.id !== role.id);
        this.loadAvailableRolesToAssign();
    }

    salvarRolesPendentes() {
        const userId = Number(this.selectedUser?.id ?? this.selectedUser?.Id ?? 0);
        const roleIds = this.pendingRoles.map(r => r.id).filter(id => !!id);

        if (!userId) {
            this.poNotification.warning('Não foi possível identificar o usuário.');
            return;
        }
        if (roleIds.length === 0) {
            this.poNotification.warning('Selecione ao menos uma role para salvar.');
            return;
        }
        if (this.savingRoles) {
            return;
        }

        this.savingRoles = true;
        this.userService.assignRolesToUserBatch(userId, roleIds).pipe(
            finalize(() => {
                this.savingRoles = false;
            })
        ).subscribe({
            next: (response) => {
                this.pendingRoles = [];
                this.assignRolesModal.close();
                this.loadRolesByUser(userId);
                this.exibirSucessoAtribuicaoRoles(response, roleIds.length);
            },
            error: () => {
                this.poNotification.error('Não foi possível salvar as roles. Tente novamente.');
            }
        });
    }

    private exibirSucessoAtribuicaoRoles(response: AssignRolesBatchResponse, totalRoles: number) {
        const mensagemApi = String(response?.message ?? '').trim();
        if (mensagemApi) {
            this.poNotification.success(mensagemApi);
            return;
        }

        const novosMenus = response?.novosMenus ?? [];
        if (novosMenus.length > 0) {
            const nomes = novosMenus
                .map(m => m.nome ?? '')
                .filter(Boolean)
                .join(', ');
            this.poNotification.success(
                `${totalRoles} role(s) atribuída(s). Novos menus liberados: ${nomes}. O usuário será notificado por e-mail.`
            );
            return;
        }

        this.poNotification.success(
            `${totalRoles} role(s) atribuída(s) com sucesso. O usuário será notificado por e-mail sobre os novos acessos.`
        );
    }

    removeRole(role: RoleItem) {
        const userId = Number(this.selectedUser?.id ?? this.selectedUser?.Id ?? 0);
        const roleId = String(role.id ?? '').trim();
        const emptyGuid = '00000000-0000-0000-0000-000000000000';
        if (!userId || !roleId || roleId === emptyGuid) {
            this.poNotification.warning(
                'Não foi possível identificar a role no catálogo para remover. Recarregue a lista e tente novamente.'
            );
            return;
        }
        this.userService.removeRoleFromUser(userId, roleId).subscribe({
            next: () => {
                this.poNotification.success(`Role "${role.name}" removida do usuário.`);
                this.loadRolesByUser(userId, () => this.loadAvailableRolesToAssign());
            },
            error: () => this.poNotification.error('Não foi possível remover role do usuário.')
        });
    }
}
